/**
 * Poza unei semnături → PNG cu fundal transparent, decupat.
 *
 * Alexandru a trimis semnătura lui Victor ca fotografie a unei foi: hârtie gri, semnătura undeva
 * pe la mijloc, restul spațiu gol. Pusă așa pe un proces-verbal, ar fi acoperit rubrica cu un
 * dreptunghi gri și ar fi lăsat semnătura minusculă în mijlocul lui — exact ce s-a și întâmplat
 * la prima încercare, cu praguri fixe.
 *
 * FONDUL SE MĂSOARĂ, NU SE PRESUPUNE. Prima variantă socotea „hârtie" tot ce e mai luminos de 235.
 * Fotografia avea hârtia la 177 — lumină de birou, telefon ținut în mână — deci fiecare pixel de
 * hârtie a ieșit pe jumătate opac și toată foaia a intrat în decupaj. Acum fondul e mediana
 * imaginii: dacă hârtia e gri, pragurile coboară cu ea.
 *
 * Trei operații, în ordinea asta:
 *
 *  1. **transparență din contrastul cu fondul măsurat** — cerneala e mai închisă decât hârtia,
 *     indiferent de culoarea ei; se păstrează culoarea reală a cernelii, ca semnătura să arate ca
 *     pe hârtie, nu ca o siluetă neagră;
 *  2. **decupare** la dreptunghiul care conține cerneala, cu o margine mică;
 *  3. **micșorare** la o înălțime rezonabilă — o semnătură de 3000 px nu se vede mai bine pe un
 *     PDF unde ocupă 40 de puncte, doar umflă documentul de zece ori.
 *
 * Totul în browser: fotografia nu pleacă nicăieri până nu e curățată, iar ce ajunge în baza de
 * date e strict semnătura, nu și fondul camerei în care a fost făcută poza.
 */

/** Cât sub fond începe cerneala, și cât mai jos e considerată plină. Fracțiuni din fond. */
const MARJA_FOND = 0.08
const SATURATIE = 0.45
/** Alfa de la care un pixel intră în dreptunghiul de decupare. Sub ea e umbra hârtiei. */
const PRAG_DECUPARE = 40
const INALTIME_MAXIMA = 240
const MARGINE = 8

export async function semnaturaDinPoza(fisier: File): Promise<string> {
  const img = await incarca(fisier)

  const c = document.createElement('canvas')
  c.width = img.naturalWidth
  c.height = img.naturalHeight
  const ctx = c.getContext('2d')
  if (!ctx) throw new Error('Browserul nu poate procesa imaginea')
  ctx.drawImage(img, 0, 0)

  const d = ctx.getImageData(0, 0, c.width, c.height)
  const p = d.data

  /*
    O imagine care ARE deja transparență e o semnătură curățată o dată. Nu se mai curăță: se
    decupează și se micșorează, atât.

    Prima încercare de aici a socotit fondul ca mediana pixelilor vizibili — ceea ce, pe un PNG
    curat, măsoară CERNEALA, nu hârtia. Fondul a ieșit 39 în loc de 177, tot ce era mai luminos a
    intrat la „hârtie", și imaginea s-a golit: „pare o pagină goală". Un PNG cu alfa a trecut deja
    prin pasul ăsta; a-l trece încă o dată nu-l poate face decât mai rău.
  */
  let transparenti = 0
  for (let i = 3; i < p.length; i += 4) if (p[i] < 250) transparenti++
  const areTransparenta = transparenti > (p.length / 4) * 0.05

  /*
    Altfel, fondul se măsoară: mediana luminanței întregii imagini. Pe o fotografie a unei foi,
    hârtia ocupă covârșitor mai mult decât cerneala, deci mediana E hârtia — oricât de gri ar fi.
    Pragurile fixe au fost prima greșeală: hârtia din poza lui Victor stă la 177, nu la 235.
  */
  let sus = 0
  let interval = 1
  if (!areTransparenta) {
    const luminante: number[] = []
    for (let i = 0; i < p.length; i += 4) {
      luminante.push(0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2])
    }
    luminante.sort((a, b) => a - b)
    const fond = luminante[Math.floor(luminante.length / 2)]
    sus = fond * (1 - MARJA_FOND)
    interval = Math.max(1, sus - fond * (1 - SATURATIE))
  }

  let x0 = c.width
  let y0 = c.height
  let x1 = -1
  let y1 = -1

  for (let i = 0; i < p.length; i += 4) {
    let alfa = p[i + 3]
    if (!areTransparenta) {
      const lum = 0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2]
      alfa = Math.max(0, Math.min(255, Math.round(((sus - lum) / interval) * 255)))
      p[i + 3] = alfa
    }
    if (alfa > PRAG_DECUPARE) {
      const px = (i / 4) % c.width
      const py = Math.floor(i / 4 / c.width)
      if (px < x0) x0 = px
      if (px > x1) x1 = px
      if (py < y0) y0 = py
      if (py > y1) y1 = py
    }
  }
  if (x1 < 0) throw new Error('Nu am găsit nicio semnătură în imagine — pare o pagină goală.')
  ctx.putImageData(d, 0, 0)

  x0 = Math.max(0, x0 - MARGINE)
  y0 = Math.max(0, y0 - MARGINE)
  x1 = Math.min(c.width - 1, x1 + MARGINE)
  y1 = Math.min(c.height - 1, y1 + MARGINE)

  const latime = x1 - x0 + 1
  const inaltime = y1 - y0 + 1
  const scara = inaltime > INALTIME_MAXIMA ? INALTIME_MAXIMA / inaltime : 1

  const out = document.createElement('canvas')
  out.width = Math.max(1, Math.round(latime * scara))
  out.height = Math.max(1, Math.round(inaltime * scara))
  const octx = out.getContext('2d')
  if (!octx) throw new Error('Browserul nu poate procesa imaginea')
  octx.imageSmoothingQuality = 'high'
  octx.drawImage(c, x0, y0, latime, inaltime, 0, 0, out.width, out.height)

  const url = out.toDataURL('image/png')
  if (url.length > 300_000) {
    throw new Error('Semnătura iese prea mare. Fotografiaz-o mai de aproape, pe hârtie albă.')
  }
  return url
}

function incarca(fisier: File): Promise<HTMLImageElement> {
  return new Promise((rezolva, respinge) => {
    const url = URL.createObjectURL(fisier)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      rezolva(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      respinge(new Error('Fișierul nu se poate deschide ca imagine'))
    }
    img.src = url
  })
}
